<template>
  <div class="config-bank-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="config-container">
      <!-- Header Section -->
      <div class="config-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="$router.go(-1)">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-handshake title-icon"></i>
              Conciliação Bancária
            </h1>
            <p class="page-subtitle">
              Configure as opções de conciliação bancária automática
            </p>
          </div>

          <div class="actions-section"></div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="config-content">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <!-- Configuration Card -->
          <div class="config-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-cog mr-2"></i>
                Configurações de Conciliação
              </h3>
              <p class="card-description">
                Defina os parâmetros para sugestões automáticas de conciliação
              </p>
            </div>

            <div class="card-content">
              <!-- Info Alert -->
              <div class="info-alert">
                <div class="alert-icon">
                  <i class="fas fa-info-circle"></i>
                </div>
                <div class="alert-content">
                  <h4 class="alert-title">Sobre a Conciliação Automática</h4>
                  <p class="alert-text">
                    Quando habilitada, o sistema sugerirá automaticamente
                    correspondências entre lançamentos bancários e lançamentos
                    financeiros, mesmo quando houver pequenas diferenças de
                    valores dentro da margem configurada.
                  </p>
                </div>
              </div>

              <!-- Form Grid -->
              <div class="form-grid">
                <!-- Enable Suggestion -->
                <div class="form-group full-width">
                  <label class="form-label">
                    <i class="fas fa-toggle-on mr-2"></i>
                    Habilitar Sugestão de Conciliação com Diferença
                  </label>
                  <div class="toggle-options">
                    <div
                      class="toggle-option"
                      :class="{
                        active: bank_reconciliation_suggestion === 'NAO',
                      }"
                      @click="bank_reconciliation_suggestion = 'NAO'"
                    >
                      <div class="option-icon">
                        <i class="fas fa-times-circle"></i>
                      </div>
                      <span class="option-label">Desabilitado</span>
                    </div>
                    <div
                      class="toggle-option"
                      :class="{
                        active: bank_reconciliation_suggestion === 'SIM',
                      }"
                      @click="bank_reconciliation_suggestion = 'SIM'"
                    >
                      <div class="option-icon">
                        <i class="fas fa-check-circle"></i>
                      </div>
                      <span class="option-label">Habilitado</span>
                    </div>
                  </div>
                </div>

                <!-- Percentage Input (only when enabled) -->
                <div
                  v-if="bank_reconciliation_suggestion === 'SIM'"
                  class="form-group full-width percentage-section"
                >
                  <label class="form-label">
                    <i class="fas fa-percent mr-2"></i>
                    Diferença Máxima Permitida (%)
                  </label>
                  <div class="percentage-input-wrapper">
                    <input
                      type="number"
                      class="modern-form-input percentage-input"
                      v-model="bank_reconciliation_suggestion_percentage"
                      placeholder="Ex: 5"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                  <p class="input-hint">
                    <i class="fas fa-lightbulb mr-1"></i>
                    O sistema sugerirá conciliações com diferença de até
                    {{ bank_reconciliation_suggestion_percentage || 0 }}% entre
                    os valores
                  </p>

                  <!-- Example Card -->
                  <div class="example-card">
                    <div class="example-header">
                      <i class="fas fa-calculator mr-2"></i>
                      Exemplo de Aplicação
                    </div>
                    <div class="example-content">
                      <div class="example-row">
                        <span class="example-label">Valor do Lançamento:</span>
                        <span class="example-value">R$ 1.000,00</span>
                      </div>
                      <div class="example-row">
                        <span class="example-label">Diferença Permitida:</span>
                        <span class="example-value highlight">
                          ±
                          {{
                            calculateExampleDifference(
                              bank_reconciliation_suggestion_percentage
                            )
                          }}
                        </span>
                      </div>
                      <div class="example-row">
                        <span class="example-label">Faixa de Valores:</span>
                        <span class="example-value">
                          R$
                          {{
                            calculateMinValue(
                              bank_reconciliation_suggestion_percentage
                            )
                          }}
                          até R$
                          {{
                            calculateMaxValue(
                              bank_reconciliation_suggestion_percentage
                            )
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Warning when disabled -->
              <div
                v-if="bank_reconciliation_suggestion === 'NAO'"
                class="warning-card"
              >
                <div class="warning-icon">
                  <i class="fas fa-exclamation-triangle"></i>
                </div>
                <div class="warning-content">
                  <h4 class="warning-title">
                    Sugestão Automática Desabilitada
                  </h4>
                  <p class="warning-text">
                    Com esta opção desabilitada, o sistema apenas sugerirá
                    conciliações quando os valores forem exatamente iguais.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Errors Section -->
          <div class="mt-2 mb-2">
            <ErrorsMessages :errors="errors" />
          </div>

          <!-- Actions Footer -->
          <div class="actions-footer">
            <ConfirmationDialog
              message-title="Confirmar Alterações"
              message-body="Deseja salvar as configurações de conciliação bancária?"
              open-class="btn-save-modern"
              open-text="Salvar Configurações"
              openLeftIcon="fa-save"
              @yes="save"
            />
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import ConfirmationDialog from "../templates/Components/ConfirmationDialog";

import api from "../services/api";
import toast from "../lib/ToastNotification";

export default {
  name: "ConfigBankReconciliation",

  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    ConfirmationDialog,
  },

  data() {
    return {
      errors: [],
      bank_reconciliation_suggestion: "NAO",
      bank_reconciliation_suggestion_percentage: 0,
      loading: false,
    };
  },

  created() {
    this.retrieve();
  },

  methods: {
    async retrieve() {
      await this.get();
    },

    async get() {
      try {
        this.loading = true;

        const response = await api.get("/api/transactions/settings");

        const {
          bank_reconciliation_suggestion,
          bank_reconciliation_suggestion_percentage,
        } = response.data;

        this.bank_reconciliation_suggestion = bank_reconciliation_suggestion;
        this.bank_reconciliation_suggestion_percentage =
          bank_reconciliation_suggestion_percentage;
      } catch (error) {
        console.error(error);
        this.errors = ["Erro ao carregar configurações. Tente novamente."];
      } finally {
        this.loading = false;
      }
    },

    async save() {
      try {
        this.loading = true;

        let {
          bank_reconciliation_suggestion,
          bank_reconciliation_suggestion_percentage,
        } = this;

        const data = {
          bank_reconciliation_suggestion,
          bank_reconciliation_suggestion_percentage,
        };

        const response = await api.patch("/api/transactions/settings", data);

        const { error, errors, message } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          toast(message, "success");
          this.retrieve();
        }
      } catch (error) {
        console.error(error);
        this.errors = ["Erro ao salvar configurações. Tente novamente."];
      } finally {
        this.loading = false;
      }
    },

    calculateExampleDifference(percentage) {
      const baseValue = 1000;
      const difference = (baseValue * (percentage || 0)) / 100;
      return `R$ ${difference.toFixed(2).replace(".", ",")}`;
    },

    calculateMinValue(percentage) {
      const baseValue = 1000;
      const difference = (baseValue * (percentage || 0)) / 100;
      const minValue = baseValue - difference;
      return minValue.toFixed(2).replace(".", ",");
    },

    calculateMaxValue(percentage) {
      const baseValue = 1000;
      const difference = (baseValue * (percentage || 0)) / 100;
      const maxValue = baseValue + difference;
      return maxValue.toFixed(2).replace(".", ",");
    },
  },
};
</script>

<style scoped>
.config-bank-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.config-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.config-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Content Section */
.config-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

.loading-overlay {
  border-radius: 16px;
}

/* Configuration Card */
.config-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.card-title i {
  color: #0f65b0;
}

.card-description {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.card-content {
  padding: 32px 24px;
}

/* Info Alert */
.info-alert {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135dg, #dbeafe 0%, #bfdbfe 100%);
  border: 1px solid #60a5fa;
  border-radius: 12px;
  margin-bottom: 32px;
}

.alert-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.alert-content {
  flex: 1;
}

.alert-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 8px 0;
}

.alert-text {
  font-size: 14px;
  color: #1e40af;
  margin: 0;
  line-height: 1.6;
}

/* Form Grid */
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group.full-width {
  width: 100%;
}

.form-label {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
}

.form-label i {
  color: #6b7280;
}

/* Toggle Options */
.toggle-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.toggle-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-option:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.toggle-option.active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: #3b82f6;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.2);
}

.option-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: #e5e7eb;
  color: #6b7280;
  transition: all 0.3s ease;
}

.toggle-option.active .option-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.option-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.toggle-option.active .option-label {
  color: #1e40af;
}

/* Percentage Section */
.percentage-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  animation: slideIn 0.4s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.percentage-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.modern-form-input {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  padding: 14px 50px 14px 16px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
  background: white !important;
  width: 100%;
}

.modern-form-input:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

.input-suffix {
  position: absolute;
  right: 16px;
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
}

.input-hint {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #6b7280;
  margin: 4px 0 0 0;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.input-hint i {
  color: #f59e0b;
  margin-right: 4px;
}

/* Example Card */
.example-card {
  background: white;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  overflow: hidden;
  margin-top: 20px;
}

.example-header {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 16px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  border-bottom: 2px solid #e5e7eb;
}

.example-header i {
  color: #0f65b0;
}

.example-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.example-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.example-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.example-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.example-value.highlight {
  color: #0f65b0;
  font-size: 16px;
}

/* Warning Card */
.warning-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  margin-top: 24px;
}

.warning-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 8px 0;
}

.warning-text {
  font-size: 14px;
  color: #92400e;
  margin: 0;
  line-height: 1.6;
}

/* Actions Footer */
.actions-footer {
  display: flex;
  justify-content: flex-end;
  padding: 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Override Confirmation Dialog Button */
.actions-footer >>> .btn-save-modern {
  background: linear-gradient(135deg, #1e884d 0%, #16a34a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 14px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(30, 136, 77, 0.3) !important;
  transition: all 0.3s ease !important;
  font-size: 16px !important;
}

.actions-footer >>> .btn-save-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(30, 136, 77, 0.4) !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .page-title {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .config-container {
    padding: 0 16px;
  }

  .config-header {
    padding: 20px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .card-header,
  .card-content {
    padding: 20px 16px;
  }

  .toggle-options {
    grid-template-columns: 1fr;
  }

  .percentage-section {
    padding: 20px 16px;
  }

  .info-alert,
  .warning-card {
    flex-direction: column;
    padding: 16px;
  }

  .alert-icon,
  .warning-icon {
    margin: 0 auto;
  }

  .alert-content,
  .warning-content {
    text-align: center;
  }

  .example-row {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .actions-footer {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .page-subtitle {
    font-size: 14px;
    text-align: center;
  }

  .btn-back-modern {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }

  .card-title {
    font-size: 18px;
  }

  .toggle-option {
    padding: 20px 16px;
  }

  .option-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .actions-footer >>> .btn-save-modern {
    width: 100%;
    padding: 12px 24px !important;
    font-size: 14px !important;
  }
}
</style>